import { useRef, useState } from "react";
import { Download, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export function BulkImport({ entity, templatePath, importPath, onDone }) {
  const inputRef = useRef();
  const [downloading, setDownloading] = useState(false);
  const [importing, setImporting] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get(templatePath, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${entity}_import_template.xlsx`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      // Defer cleanup — revoking the object URL synchronously cancels the download in some browsers / PWA.
      setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 4000);
      toast.success("Template downloaded");
    } catch (e) {
      let msg = "Download failed";
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { msg = JSON.parse(txt).detail || msg; } catch { msg = txt || msg; }
        } else { msg = apiError(e); }
      } catch { msg = apiError(e); }
      toast.error(msg);
    } finally { setDownloading(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(importPath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const errs = data.errors?.length || 0;
      toast.success(`Imported ${data.created} ${entity}${data.created === 1 ? "" : "s"}${errs ? ` · ${errs} row(s) skipped` : ""}`);
      if (data.not_in_universe) toast.warning(`${data.not_in_universe} imported ${data.not_in_universe === 1 ? "row is" : "rows are"} not in the Universe — open each record and use “Add to Universe” to link the company.`);
      if (errs) console.warn(`${entity} import skipped rows:`, data.errors);
      onDone?.();
    } catch (e) { toast.error(apiError(e)); } finally {
      setImporting(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <>
      <Button variant="outline" onClick={download} disabled={downloading} className="gap-1.5" data-testid={`${entity}-template-btn`}>
        {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Template
      </Button>
      <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={importing} className="gap-1.5" data-testid={`${entity}-import-btn`}>
        {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import
      </Button>
      <input ref={inputRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid={`${entity}-import-input`} />
    </>
  );
}
